import React from 'react';

import JSONLDUtils from 'common/util/JsonLdUtils';

import Helmet from 'common/helmets/Helmet';

const Title = 'Pricing | Canny';
const Description =
  'Canny costs less than building the wrong features. Canny pricing works for everyone - big or small, b2b or consumer. Get started for free!';
const Image = 'https://canny.io/images/canny-social.png';
const PricingUrl = 'https://canny.io/pricing';

const jsonLdGraph = JSONLDUtils.createGraph([
  JSONLDUtils.createWebPage({
    '@id': `${PricingUrl}#webpage`,
    url: PricingUrl,
    name: 'Canny Pricing',
    description:
      'Compare Canny pricing plans: Free, Core, Pro, and Business. Choose the right plan to scale feedback management with your team.',
    inLanguage: 'en',
    about: { '@id': `${PricingUrl}#product` },
    isPartOf: { '@id': 'https://canny.io/#website' },
  }),
  JSONLDUtils.createProduct({
    '@id': `${PricingUrl}#product`,
    name: 'Canny',
    description:
      'Canny is a feedback management platform with flexible plans for teams of all sizes.',
    image: ['https://canny.io/images/canny-social.png'],
    brand: { '@id': 'https://canny.io/#organization' },
    url: PricingUrl,
    offers: [
      {
        '@type': 'Offer',
        name: 'Free',
        url: PricingUrl,
        availability: 'https://schema.org/InStock',
        priceSpecification: {
          '@type': 'UnitPriceSpecification',
          price: '0',
          priceCurrency: 'USD',
          billingIncrement: 'monthly',
          billingDuration: 'P1M',
        },
        description:
          'Free plan with 25 tracked users, unlimited posts, and automatic feedback capture.',
      },
      {
        '@type': 'Offer',
        name: 'Core',
        url: PricingUrl,
        availability: 'https://schema.org/InStock',
        priceSpecification: {
          '@type': 'UnitPriceSpecification',
          price: '19',
          priceCurrency: 'USD',
          billingIncrement: 'monthly',
          billingDuration: 'P1M',
        },
        description: 'Includes 100+ tracked users, custom domains, and content translations.',
      },
      {
        '@type': 'Offer',
        name: 'Pro',
        url: PricingUrl,
        availability: 'https://schema.org/InStock',
        priceSpecification: {
          '@type': 'UnitPriceSpecification',
          price: '79',
          priceCurrency: 'USD',
          billingIncrement: 'monthly',
          billingDuration: 'P1M',
        },
        description: 'Includes all Core features plus PM integrations and advanced privacy.',
      },
      {
        '@type': 'Offer',
        name: 'Business',
        url: PricingUrl,
        availability: 'https://schema.org/InStock',
        description:
          'Custom pricing for enterprises with 5,000+ tracked users, SSO, and CRM integrations.',
      },
    ],
  }),
  JSONLDUtils.createBreadcrumbList([
    { name: 'Home', item: 'https://canny.io/' },
    { name: 'Pricing', item: PricingUrl },
  ]),
]);

export default function PricingV4Helmet() {
  return (
    <Helmet
      title={Title}
      meta={[
        { name: 'description', content: Description },

        { property: 'og:image', content: Image },
        { property: 'og:title', content: Title },
        { property: 'og:description', content: Description },
        { property: 'og:url', content: PricingUrl },
        { property: 'og:type', content: 'website' },

        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:site', content: '@cannyHQ' },
        { name: 'twitter:title', content: Title },
        { name: 'twitter:description', content: Description },
        { name: 'twitter:image', content: Image },
      ]}>
      <script type="application/ld+json">{JSONLDUtils.toString(jsonLdGraph)}</script>
    </Helmet>
  );
}
